import React from "react";
import { motion } from "framer-motion";
import SectionHeader from "../SectionHeader";
import type { HardwareFeature, ColorTheme } from "./deta-HardwareFeatures/types";

interface FeatureProps {
    image: string;
    className?: string;
    color: string;
    index: number;
    colorTheme: ColorTheme;
}

interface HardwareFeaturesProps {
    features: HardwareFeature[];
    colorTheme: ColorTheme;
    regularText?: string;
    highlightedText?: string;
    descriptionText?: string;
}

const FeatureCard: React.FC<FeatureProps> = ({ image, className, color, index, colorTheme }) => {
    const currentColor = colorTheme[color] || colorTheme.blue;
    const gradientClass = currentColor.gradient;
    const rgbValue = currentColor.rgb;

    return (
        <motion.div
            className={`relative group ${className} cursor-pointer`}
            initial={{
                opacity: 0,
                y: 60,
                scale: 0.8,
                rotateX: 45,
            }}
            whileInView={{
                opacity: 1,
                y: 0,
                scale: 1,
                rotateX: 0,
            }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{
                duration: 0.8,
                delay: index * 0.15,
                type: "spring",
                stiffness: 100,
                damping: 15,
            }}
            whileHover={{
                y: -12,
                scale: 1.02,
                rotateY: 2,
                transition: {
                    duration: 0.4,
                    type: "spring",
                    stiffness: 300,
                    damping: 20,
                },
            }}
            whileTap={{
                scale: 0.98,
                transition: { duration: 0.1 },
            }}
        >
            {/* کارت اصلی */}
            <motion.div
                className="rounded-[32px] hover:shadow-2xl transition-all duration-500  bg-[#F6FAFF]  border-2 border-[#EAEDF2] h-full relative z-10 overflow-hidden backdrop-blur-sm"
                whileHover={{
                    boxShadow: `0 25px 50px -12px rgba(${rgbValue}, 0.25)`,
                    transition: { duration: 0.3 },
                }}
            >
                {/* شیمر افکت */}
                <motion.div
                    className="absolute inset-0 "
                    animate={{
                        translateX: ["100%", "-100%"],
                    }}
                    transition={{
                        duration: 2,
                        delay: index * 0.2 + 1,
                        ease: "easeInOut",
                    }}
                />

                {/* تصویر */}
                <div className="relative z-10 h-full w-full md:scale-[1.02] bg-[#F6FAFF]">
                    <motion.div
                        className="h-full w-full  overflow-hidden"
                        whileHover={{
                            scale: 1.04,
                            rotate: 1,
                        }}
                        transition={{
                            duration: 0.4,
                            type: "spring",
                            stiffness: 200,
                        }}
                    >
                        <motion.img
                            src={image}
                            alt="Hardware Feature"
                            className="w-full h-full object-cover"
                            initial={{ scale: 1.1, opacity: 0 }}
                            whileInView={{ scale: 1, opacity: 1 }}
                            transition={{
                                duration: 0.6,
                                delay: index * 0.1 + 0.3,
                            }}
                        />

                        {/* اورلی گرادیانت */}
                        <motion.div className={`absolute inset-0 bg-gradient-to-t ${gradientClass} opacity-0`} whileHover={{ opacity: 0.1 }} transition={{ duration: 0.3 }} />
                    </motion.div>
                </div>

                {/* نقاط تزئینی */}
                <motion.div
                    className="absolute top-4 right-4 w-2 h-2 rounded-full bg-white/60"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.6, 1, 0.6],
                    }}
                    transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: index * 0.3,
                    }}
                />
            </motion.div>

            {/* سایه گلو */}
            <motion.div
                className={`absolute inset-0 rounded-[32px] bg-gradient-to-r ${gradientClass} blur-2xl z-0 opacity-0`}
                initial={{ scale: 0.8, opacity: 0 }}
                whileHover={{
                    scale: 1.1,
                    opacity: 0.2,
                }}
                transition={{
                    duration: 0.4,
                    ease: "easeOut",
                }}
            />

            {/* حلقه‌های انیمیشنی */}
            <motion.div
                className="absolute inset-0 rounded-[32px] border-2 border-transparent opacity-0"
                style={{
                    borderImage: `linear-gradient(45deg, transparent, rgb(${rgbValue}), transparent) 1`,
                }}
                whileHover={{
                    opacity: 1,
                    scale: [1, 1.05, 1],
                }}
                transition={{
                    duration: 0.6,
                    ease: "easeInOut",
                }}
            />
        </motion.div>
    );
};

const HardwareFeatures: React.FC<HardwareFeaturesProps> = ({
    features,
    colorTheme,
    regularText = "ویژگی سخت افزاری",
    highlightedText = "حلقه",
    descriptionText = "فناوری پیشرفته در کوچک‌ترین ابعاد ممکن",
}) => {
    return (
        <section dir="ltr" className="pb-32 relative overflow-hidden">
           

            <div className="container mx-auto px-4 sm:px-10 relative z-10">
                {/* هدر با انیمیشن */}
                <motion.div className="mb-6 md:mb-12" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                    <SectionHeader regularText={regularText} highlightedText={highlightedText} descriptionText={descriptionText} className="mb-12" />
                </motion.div>

                {/* گرید ویژگی‌ها */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{
                        duration: 0.8,
                        type: "spring",
                        stiffness: 100,
                    }}
                    className="grid grid-cols-12 md:grid-rows-5 gap-6 max-w-6xl mx-auto md:h-[600px] auto-rows md:auto-rows-fr"
                >
                    {features.map((feature, index) => (
                        <FeatureCard key={index} image={feature.image} className={feature.className} color={feature.color} index={index} colorTheme={colorTheme} />
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default HardwareFeatures;
